import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import StorageIcon from "@mui/icons-material/Storage";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useRef } from "react";
import DrawerComponentView from "src/app/components/drawer/drawer.view";
import DrawerAwareContainerComponentView from "src/app/components/drawerAwareContainer/drawerAwareContainer.view";
import SnackBarComponentView from "src/app/components/snackbar/snackbar.view";
import {
  File,
  FilesStorageRequest,
  Folder,
} from "src/app/models/screens/files.model";
import { RequestManagementUser } from "src/app/models/screens/requestManagement.model";
import FilesScreenViewModel from "src/app/screens/files/files.viewmodel";
import styles from "src/app/styles/screens/files.style";

const fileIconColor = (mimeType: string): string => {
  if (mimeType.startsWith("image/")) return "#e91e63";
  if (mimeType.startsWith("video/")) return "#9c27b0";
  if (mimeType.startsWith("audio/")) return "#ff9800";
  if (mimeType.includes("pdf")) return "#f44336";
  if (mimeType.includes("spreadsheet") || mimeType.includes("excel"))
    return "#4caf50";
  if (mimeType.includes("word") || mimeType.includes("document"))
    return "#2196f3";
  return "#607d8b";
};

const statusColor = (status: string): "warning" | "success" | "error" => {
  if (status === "Accepted") return "success";
  if (status === "Rejected") return "error";
  return "warning";
};

const FilesScreenView: React.FC = () => {
  const vm = FilesScreenViewModel();
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <Box sx={styles.parent}>
      <DrawerComponentView selectedId="Files" />
      <DrawerAwareContainerComponentView
        drawerWidth={250}
        sx={styles.drawerAwareContainer}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={(e) => vm.onFilesSelected(e.target.files)}
          onClick={(e) => ((e.target as HTMLInputElement).value = "")}
        />

        <Grid container justifyContent="center" spacing={3}>
          <Grid
            size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}
            sx={styles.titleGrid}
          >
            <Typography component="span" sx={styles.titleText}>
              Files
            </Typography>
          </Grid>

          {vm.userRole === "Parent" && (
            <Grid size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}>
              <Grid
                size={{ xl: 4, lg: 5, md: 5, sm: 6, xs: 7 }}
                sx={styles.selectorContainer}
                justifySelf="flex-end"
              >
                <Paper elevation={0} sx={styles.selectorPaper}>
                  <FormControl fullWidth>
                    <InputLabel id="files-owner-label">Viewing as</InputLabel>
                    <Select
                      labelId="files-owner-label"
                      value={vm.selectedOwnerId}
                      label="Viewing as"
                      onChange={(e: SelectChangeEvent) =>
                        vm.onOwnerChanged(e.target.value)
                      }
                      disabled={vm.isLoadingChildren}
                    >
                      <MenuItem value="you">You</MenuItem>
                      {vm.children.map((child) => (
                        <MenuItem key={child._id} value={child._id}>
                          {child.firstName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>
            </Grid>
          )}
          {vm.userRole === "Admin" && (
            <Grid size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}>
              <Grid
                size={{ xl: 4, lg: 5, md: 5, sm: 6, xs: 7 }}
                sx={styles.selectorContainer}
                justifySelf="flex-end"
              >
                <Paper elevation={0} sx={styles.selectorPaper}>
                  <FormControl fullWidth>
                    <InputLabel id="files-admin-user-label">
                      Viewing Files of
                    </InputLabel>
                    <Select
                      labelId="files-admin-user-label"
                      value={vm.selectedOwnerId}
                      label="Viewing Files of"
                      onChange={(e: SelectChangeEvent) =>
                        vm.onOwnerChanged(e.target.value)
                      }
                      disabled={vm.isLoadingAdminUsers}
                    >
                      <MenuItem value="you">You</MenuItem>
                      {vm.adminUsers.map((u: RequestManagementUser) => (
                        <MenuItem key={u.id} value={u.id}>
                          {`${u.firstName} ${u.lastName}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>
            </Grid>
          )}
          <Grid size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}>
            <Paper elevation={0} sx={styles.tabsContainer}>
              {vm.userRole === "Admin" && (
                <Tabs
                  value={vm.selectedTab}
                  onChange={vm.onTabChanged}
                  variant="fullWidth"
                >
                  <Tab
                    icon={<FolderIcon />}
                    iconPosition="start"
                    label="File Manager"
                  />
                  <Tab
                    icon={<StorageIcon />}
                    iconPosition="start"
                    label="Storage Requests"
                  />
                </Tabs>
              )}
              <Divider />
              {vm.selectedTab === 0 && (
                <Box sx={styles.selectedTab}>
                  {vm.storageInfo && (
                    <Box sx={styles.storageBarContainer}>
                      <StorageIcon fontSize="small" sx={{ opacity: 0.6 }} />
                      <Box sx={styles.storageBarTrack}>
                        <Box
                          sx={{
                            width: `${vm.storageUsedPct}%`,
                            height: "100%",
                            borderRadius: 4,
                            backgroundColor:
                              vm.storageUsedPct > 85
                                ? "#f44336"
                                : vm.storageUsedPct > 65
                                  ? "#ff9800"
                                  : "#1976d2",
                            transition: "width 0.4s",
                          }}
                        />
                      </Box>
                      <Typography sx={{ fontSize: 13, whiteSpace: "nowrap" }}>
                        {vm.formatBytes(vm.storageInfo.usedBytes)} /{" "}
                        {vm.formatBytes(vm.storageInfo.maxBytes)}
                      </Typography>
                      {!vm.isViewingAsAdmin && (
                        <Tooltip title="Request more storage">
                          <IconButton
                            size="small"
                            onClick={vm.openStorageRequestDialog}
                          >
                            <ExpandMoreIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                  <Box sx={styles.toolbarRow}>
                    {!vm.isViewingAsAdmin && (
                      <>
                        <Button
                          variant="contained"
                          startIcon={<CreateNewFolderIcon />}
                          onClick={vm.openCreateFolderDialog}
                          size="small"
                        >
                          New Folder
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<CloudUploadIcon />}
                          onClick={() => fileInputRef.current?.click()}
                          size="small"
                        >
                          Upload Files
                        </Button>
                      </>
                    )}
                    {vm.isViewingAsAdmin && (
                      <Chip
                        label="View only — Admin mode"
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    )}
                  </Box>
                  <Box sx={styles.breadcrumbContainer}>
                    {vm.breadcrumbs.map((crumb, idx) => {
                      const isLast = idx === vm.breadcrumbs.length - 1;
                      return (
                        <Box
                          key={crumb.id ?? "root"}
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          {idx > 0 && (
                            <ChevronRightIcon
                              sx={{ fontSize: 16, opacity: 0.4, mx: 0.25 }}
                            />
                          )}
                          <Typography
                            onClick={
                              !isLast
                                ? () => vm.onBreadcrumbClicked(crumb)
                                : undefined
                            }
                            sx={
                              isLast
                                ? styles.breadcrumbActive
                                : styles.breadcrumbItem
                            }
                          >
                            {crumb.name}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                  {vm.isLoadingItems ? (
                    <Box sx={styles.loadingContainer}>
                      <CircularProgress size={32} />
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      {vm.folders.map((folder: Folder) => (
                        <Grid
                          key={folder.id}
                          size={{ xs: 6, sm: 4, md: 3, lg: 2 }}
                        >
                          <Paper
                            elevation={0}
                            sx={styles.itemCard}
                            onDoubleClick={() => vm.onFolderOpen(folder)}
                          >
                            <Box sx={styles.itemCardHeader}>
                              <FolderIcon
                                sx={{ color: "#f9a825", fontSize: 28 }}
                              />
                              <Typography
                                sx={{
                                  ...styles.itemName,
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                }}
                                title={folder.name}
                              >
                                {folder.name}
                              </Typography>
                            </Box>
                            <Typography sx={styles.itemMeta}>
                              {new Date(folder.createdAt).toLocaleDateString()}
                            </Typography>
                            <Box sx={styles.itemActionsRow}>
                              <Tooltip title="Open">
                                <IconButton
                                  size="small"
                                  onClick={() => vm.onFolderOpen(folder)}
                                >
                                  <ChevronRightIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {!vm.isViewingAsAdmin && (
                                <>
                                  <Tooltip title="Manage access">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        vm.openAccessDialog(folder, "folder")
                                      }
                                    >
                                      <LockOpenIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Rename">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        vm.openRenameDialog(
                                          folder.id,
                                          "folder",
                                          folder.name,
                                        )
                                      }
                                    >
                                      <DriveFileRenameOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() =>
                                        vm.openDeleteDialog(
                                          folder.id,
                                          "folder",
                                          folder.name,
                                        )
                                      }
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                              {folder.accessList.length > 0 && (
                                <Tooltip
                                  title={`Shared with ${folder.accessList.length} user(s)`}
                                >
                                  <PeopleAltOutlinedIcon
                                    sx={{ fontSize: 16, opacity: 0.5, mt: 0.5 }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                      {vm.files.map((file: File) => (
                        <Grid
                          key={file.id}
                          size={{ xs: 6, sm: 4, md: 3, lg: 2 }}
                        >
                          <Paper elevation={0} sx={styles.itemCard}>
                            <Box sx={styles.itemCardHeader}>
                              <InsertDriveFileIcon
                                sx={{
                                  color: fileIconColor(file.mimeType),
                                  fontSize: 28,
                                }}
                              />
                              <Typography
                                sx={{
                                  ...styles.itemName,
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                }}
                                title={file.name}
                              >
                                {file.name}
                              </Typography>
                            </Box>
                            <Typography sx={styles.itemMeta}>
                              {vm.formatBytes(file.sizeBytes)}
                            </Typography>
                            <Typography sx={styles.itemMeta}>
                              {new Date(file.createdAt).toLocaleDateString()}
                            </Typography>
                            <Box sx={styles.itemActionsRow}>
                              <Tooltip title="Download">
                                <IconButton
                                  size="small"
                                  onClick={() => vm.onDownloadFile(file)}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {!vm.isViewingAsAdmin && (
                                <>
                                  <Tooltip title="Manage access">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        vm.openAccessDialog(file, "file")
                                      }
                                    >
                                      <LockOpenIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Rename">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        vm.openRenameDialog(
                                          file.id,
                                          "file",
                                          file.name,
                                        )
                                      }
                                    >
                                      <DriveFileRenameOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() =>
                                        vm.openDeleteDialog(
                                          file.id,
                                          "file",
                                          file.name,
                                        )
                                      }
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                              {file.accessList.length > 0 && (
                                <Tooltip
                                  title={`Shared with ${file.accessList.length} user(s)`}
                                >
                                  <PeopleAltOutlinedIcon
                                    sx={{ fontSize: 16, opacity: 0.5, mt: 0.5 }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                      {vm.folders.length === 0 && vm.files.length === 0 && (
                        <Grid size={{ xs: 12 }}>
                          <Typography sx={styles.emptyStateText}>
                            This folder is empty.
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  )}
                </Box>
              )}
              {vm.selectedTab === 1 && vm.userRole === "Admin" && (
                <Box sx={styles.selectedTab}>
                  {vm.isLoadingVpsStorage ? (
                    <Box sx={styles.loadingContainer}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : vm.vpsStorage ? (
                    <Paper elevation={0} sx={styles.vpsCard}>
                      <Typography sx={{ fontWeight: 700, mb: 1.5 }}>
                        VPS Storage Overview
                      </Typography>
                      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                        <Box>
                          <Typography sx={{ fontSize: 12, opacity: 0.6 }}>
                            Total
                          </Typography>
                          <Typography sx={{ fontWeight: 600 }}>
                            {vm.formatBytes(vm.vpsStorage.totalBytes)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: 12, opacity: 0.6 }}>
                            Used
                          </Typography>
                          <Typography sx={{ fontWeight: 600 }}>
                            {vm.formatBytes(vm.vpsStorage.usedBytes)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: 12, opacity: 0.6 }}>
                            Free
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              color:
                                vm.vpsStorage.freeBytes <
                                vm.vpsStorage.totalBytes * 0.1
                                  ? "#f44336"
                                  : "inherit",
                            }}
                          >
                            {vm.formatBytes(vm.vpsStorage.freeBytes)}
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          ...styles.storageBarTrack,
                          mt: 1.5,
                          height: 10,
                        }}
                      >
                        <Box
                          sx={{
                            width: `${Math.round(
                              (vm.vpsStorage.usedBytes /
                                vm.vpsStorage.totalBytes) *
                                100,
                            )}%`,
                            height: "100%",
                            borderRadius: 4,
                            backgroundColor:
                              vm.vpsStorage.freeBytes <
                              vm.vpsStorage.totalBytes * 0.1
                                ? "#f44336"
                                : "#1976d2",
                          }}
                        />
                      </Box>
                    </Paper>
                  ) : null}

                  <Typography sx={{ fontWeight: 700, mb: 2 }}>
                    Pending Storage Requests
                  </Typography>

                  {vm.isLoadingStorageRequests ? (
                    <Box sx={styles.loadingContainer}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      {vm.storageRequests.map((req: FilesStorageRequest) => (
                        <Grid key={req.id} size={{ xs: 12, md: 6 }}>
                          <Paper elevation={0} sx={styles.requestCard}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 0.5,
                              }}
                            >
                              <Typography sx={{ fontWeight: 700, flex: 1 }}>
                                {req.requesterFullName}
                              </Typography>
                              <Chip
                                size="small"
                                label={req.status}
                                color={statusColor(req.status)}
                              />
                            </Box>
                            <Typography
                              sx={{ fontSize: 14, opacity: 0.7, mb: 0.5 }}
                            >
                              Requested: <strong>{req.requestedGb} GB</strong>
                            </Typography>
                            <Typography sx={{ fontSize: 12, opacity: 0.5 }}>
                              {new Date(req.createdAt).toLocaleString()}
                            </Typography>
                            {req.status === "Pending" && (
                              <Box sx={styles.requestActionsContainer}>
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  startIcon={<CheckCircleOutlineIcon />}
                                  onClick={() =>
                                    vm.onDecideStorageRequest(req.id, true)
                                  }
                                >
                                  Accept
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  startIcon={<CloseIcon />}
                                  onClick={() =>
                                    vm.onDecideStorageRequest(req.id, false)
                                  }
                                >
                                  Reject
                                </Button>
                              </Box>
                            )}
                          </Paper>
                        </Grid>
                      ))}
                      {vm.storageRequests.length === 0 && (
                        <Grid size={{ xs: 12 }}>
                          <Typography sx={styles.emptyStateText}>
                            No storage requests.
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  )}
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DrawerAwareContainerComponentView>
      {vm.uploadTasks.length > 0 && (
        <Box sx={styles.uploadPanel}>
          {vm.uploadTasks.map((task) => (
            <Paper key={task.id} elevation={0} sx={styles.uploadCard}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 0.5,
                }}
              >
                <Typography
                  sx={{ flex: 1, fontSize: 13, fontWeight: 600 }}
                  noWrap
                >
                  {task.fileName}
                </Typography>
                {(task.done || task.error) && (
                  <IconButton
                    size="small"
                    onClick={() => vm.dismissUploadTask(task.id)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              {task.error ? (
                <Typography sx={{ fontSize: 12, color: "#f44336" }}>
                  {task.error}
                </Typography>
              ) : (
                <LinearProgress
                  variant="determinate"
                  value={task.progress}
                  color={task.done ? "success" : "primary"}
                />
              )}
              {task.done && !task.error && (
                <Typography sx={{ fontSize: 11, opacity: 0.6, mt: 0.5 }}>
                  Upload complete
                </Typography>
              )}
            </Paper>
          ))}
        </Box>
      )}
      <Dialog
        open={vm.isCreateFolderOpen}
        onClose={vm.closeCreateFolderDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>New Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Folder name"
            value={vm.newFolderName}
            onChange={(e) => vm.setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") vm.onCreateFolderConfirmed();
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Box sx={styles.modalActionsContainer}>
            <Button
              variant="outlined"
              sx={styles.modalCancelButton}
              onClick={vm.closeCreateFolderDialog}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={vm.onCreateFolderConfirmed}
              loading={vm.isCreatingFolder}
            >
              Create
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!vm.renameTarget}
        onClose={vm.closeRenameDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          Rename {vm.renameTarget?.type === "folder" ? "Folder" : "File"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="New name"
            value={vm.renameName}
            onChange={(e) => vm.setRenameName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") vm.onRenameConfirmed();
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Box sx={styles.modalActionsContainer}>
            <Button
              variant="outlined"
              sx={styles.modalCancelButton}
              onClick={vm.closeRenameDialog}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={vm.onRenameConfirmed}
              loading={vm.isRenaming}
            >
              Rename
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!vm.deleteTarget}
        onClose={vm.closeDeleteDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            <strong>{vm.deleteTarget?.name}</strong>?
            {vm.deleteTarget?.type === "folder" &&
              " All contents inside will be permanently deleted."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Box sx={styles.modalActionsContainer}>
            <Button
              variant="outlined"
              sx={styles.modalCancelButton}
              onClick={vm.closeDeleteDialog}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={vm.onDeleteConfirmed}
              loading={vm.isDeleting}
            >
              Delete
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!vm.accessTarget}
        onClose={vm.closeAccessDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Manage Access — {vm.accessTarget?.name}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>
            Users with access
          </Typography>
          {vm.accessTarget?.accessList.length === 0 && (
            <Typography sx={{ fontSize: 13, opacity: 0.6, mb: 2 }}>
              No users have been granted access yet.
            </Typography>
          )}
          {vm.accessTarget?.accessList.map((entry) => (
            <Box key={entry.userId} sx={styles.accessListItem}>
              <Typography sx={{ fontSize: 14 }}>
                {entry.firstName} {entry.lastName}
              </Typography>
              <IconButton
                size="small"
                color="error"
                disabled={vm.isModifyingAccess}
                onClick={() => vm.onRemoveAccess(entry.userId)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}

          <Divider sx={{ my: 2 }} />
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Add user</Typography>
          <TextField
            fullWidth
            label="Search users"
            value={vm.accessUserSearchText}
            onChange={(e) => vm.setAccessUserSearchText(e.target.value)}
            size="small"
            sx={{ mb: 1 }}
          />
          {vm.isSearchingAccessUsers ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <List dense sx={{ p: 0 }}>
              {vm.accessSearchResults
                .filter(
                  (u: RequestManagementUser) =>
                    !vm.accessTarget?.accessList.some((a) => a.userId === u.id),
                )
                .map((u: RequestManagementUser) => (
                  <ListItemButton
                    key={u.id}
                    onClick={() => vm.onAddAccess(u)}
                    disabled={vm.isModifyingAccess}
                  >
                    <ListItemText primary={`${u.firstName} ${u.lastName}`} />
                    <LockOpenIcon fontSize="small" sx={{ opacity: 0.5 }} />
                  </ListItemButton>
                ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Box sx={{ px: 2, pb: 1.5, width: "100%" }}>
            <Button variant="outlined" fullWidth onClick={vm.closeAccessDialog}>
              Done
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <Dialog
        open={vm.isStorageRequestOpen}
        onClose={vm.closeStorageRequestDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Request More Storage</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Enter how many additional gigabytes you need. An admin will review
            your request.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Additional storage (GB)"
            type="number"
            inputProps={{ min: 1, step: 1 }}
            value={vm.storageRequestGb}
            onChange={(e) => vm.setStorageRequestGb(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Box sx={styles.modalActionsContainer}>
            <Button
              variant="outlined"
              sx={styles.modalCancelButton}
              onClick={vm.closeStorageRequestDialog}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={vm.onStorageRequestConfirmed}
              loading={vm.isSubmittingStorageRequest}
            >
              Submit
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <SnackBarComponentView
        isError={vm.isSnackbarError}
        isOpen={vm.isDisplayingSnackbar}
        message={vm.snackbarMessage}
        onClose={() => vm.setIsDisplayingSnackbar(false)}
      />
    </Box>
  );
};

export default FilesScreenView;
